'use client';

import { useState, useEffect } from 'react';

interface JsonViewerProps {
	/** The data to render. Can be any JSON-serializable value. */
	data: unknown;
	/**
	 * Base path prefix for node IDs and path tracking.
	 * Used by ProvenanceTab to set step-specific prefixes like "step_1".
	 * @default ""
	 */
	basePath?: string;
	/**
	 * Number of levels to auto-expand on initial render.
	 * @default 2
	 */
	defaultExpandDepth?: number;
	/**
	 * Maximum depth before collapsing with "..." indicators.
	 * @default 6
	 */
	maxDepth?: number;
	/**
	 * Set of JSON paths that should be visually highlighted (cyan ring).
	 * When omitted, no highlighting is applied.
	 */
	highlightedPaths?: Set<string>;
	/**
	 * Set of JSON paths that should be forcibly expanded.
	 * When omitted, only defaultExpandDepth controls expansion.
	 */
	expandedPaths?: Set<string>;
	/**
	 * Callback when an object key is clicked.
	 * When provided, object keys render as clickable buttons.
	 * When omitted, keys render as plain styled text.
	 */
	onPathClick?: (path: string) => void;
}

interface JsonNodeProps {
	data: unknown;
	path: string;
	highlightedPaths: Set<string>;
	expandedPaths: Set<string>;
	onPathClick?: (path: string) => void;
	level: number;
	defaultExpandDepth: number;
	maxDepth: number;
}

const EMPTY_SET = new Set<string>();

const HIGHLIGHT_CLASS = 'rounded-sm bg-cyan-500/20 px-1 ring-1 ring-cyan-500/60 dark:ring-cyan-400/60';
const HIGHLIGHT_ROW_CLASS = 'rounded-sm bg-cyan-500/15 -mx-1 px-1';
const GRAY_TEXT_CLASS = 'text-muted-foreground';

// eslint-disable-next-line sonarjs/cognitive-complexity
function JsonNode({
	data,
	path,
	highlightedPaths,
	expandedPaths,
	onPathClick,
	level,
	defaultExpandDepth,
	maxDepth
}: JsonNodeProps) {
	const shouldAutoExpand = expandedPaths.has(path) || level < defaultExpandDepth;
	const [expanded, setExpanded] = useState(shouldAutoExpand);
	const isHighlighted = highlightedPaths.has(path);

	useEffect(() => {
		if (expandedPaths.has(path)) setExpanded(true);
	}, [expandedPaths, path]);

	if (data === null) return <span className={GRAY_TEXT_CLASS}>null</span>;
	if (data === undefined) return <span className={GRAY_TEXT_CLASS}>undefined</span>;

	const nodeId = onPathClick ? `json-path-${path}` : undefined;

	if (typeof data === 'string') {
		return (
			<span
				id={nodeId}
				className={`break-all text-emerald-600 dark:text-emerald-400 ${isHighlighted ? HIGHLIGHT_CLASS : ''}`}
			>
				&quot;{data}&quot;
			</span>
		);
	}

	if (typeof data === 'number') {
		return (
			<span
				id={nodeId}
				className={`text-amber-600 dark:text-amber-400 ${isHighlighted ? HIGHLIGHT_CLASS : ''}`}
			>
				{data}
			</span>
		);
	}

	if (typeof data === 'boolean') {
		return (
			<span
				id={nodeId}
				className={`text-violet-600 dark:text-violet-400 ${isHighlighted ? HIGHLIGHT_CLASS : ''}`}
			>
				{data ? 'true' : 'false'}
			</span>
		);
	}

	if (Array.isArray(data)) {
		if (data.length === 0) return <span className={GRAY_TEXT_CLASS}>[]</span>;
		if (level >= maxDepth) return <span className={GRAY_TEXT_CLASS}>[...{data.length} items]</span>;

		return (
			<span id={nodeId}>
				<button
					type='button'
					onClick={() => setExpanded(!expanded)}
					className='text-muted-foreground hover:text-foreground mr-1'
				>
					{expanded ? '▼' : '▶'}
				</button>
				<span className={GRAY_TEXT_CLASS}>[</span>
				{expanded ? (
					<div className='ml-4 overflow-hidden pl-2'>
						{data.map((item, idx) => {
							const itemPath = path ? `${path}[${idx}]` : `[${idx}]`;
							return (
								<div
									key={itemPath}
									className='overflow-hidden'
								>
									<span className='text-muted-foreground mr-2'>{idx}:</span>
									<JsonNode
										data={item}
										path={itemPath}
										highlightedPaths={highlightedPaths}
										expandedPaths={expandedPaths}
										onPathClick={onPathClick}
										level={level + 1}
										defaultExpandDepth={defaultExpandDepth}
										maxDepth={maxDepth}
									/>
									{idx < data.length - 1 && <span className={GRAY_TEXT_CLASS}>,</span>}
								</div>
							);
						})}
					</div>
				) : (
					<span className={GRAY_TEXT_CLASS}>...{data.length} items</span>
				)}
				<span className={GRAY_TEXT_CLASS}>]</span>
			</span>
		);
	}

	if (typeof data === 'object') {
		const entries = Object.entries(data as Record<string, unknown>);
		if (entries.length === 0) return <span className={GRAY_TEXT_CLASS}>{'{}'}</span>;
		if (level >= maxDepth) return <span className={GRAY_TEXT_CLASS}>{'{ ... }'}</span>;

		return (
			<span id={nodeId}>
				<button
					type='button'
					onClick={() => setExpanded(!expanded)}
					className='text-muted-foreground hover:text-foreground mr-1'
				>
					{expanded ? '▼' : '▶'}
				</button>
				<span className={GRAY_TEXT_CLASS}>{'{'}</span>
				{expanded ? (
					<div className='ml-4 overflow-hidden pl-2'>
						{entries.map(([key, value], idx) => {
							const childPath = path ? `${path}.${key}` : key;
							const isChildHighlighted = highlightedPaths.has(childPath);
							return (
								<div
									key={key}
									className={`overflow-hidden ${isChildHighlighted ? HIGHLIGHT_ROW_CLASS : ''}`}
								>
									{onPathClick ? (
										<button
											type='button'
											onClick={() => onPathClick(childPath)}
											className='flex-shrink-0 text-sky-600 hover:text-sky-500 hover:underline dark:text-sky-400 dark:hover:text-sky-300'
										>
											{key}
										</button>
									) : (
										<span className='flex-shrink-0 text-sky-600 dark:text-sky-400'>{key}</span>
									)}
									<span className={GRAY_TEXT_CLASS}>: </span>
									<JsonNode
										data={value}
										path={childPath}
										highlightedPaths={highlightedPaths}
										expandedPaths={expandedPaths}
										onPathClick={onPathClick}
										level={level + 1}
										defaultExpandDepth={defaultExpandDepth}
										maxDepth={maxDepth}
									/>
									{idx < entries.length - 1 && <span className={GRAY_TEXT_CLASS}>,</span>}
								</div>
							);
						})}
					</div>
				) : (
					<span className={GRAY_TEXT_CLASS}>...{entries.length} keys</span>
				)}
				<span className={GRAY_TEXT_CLASS}>{'}'}</span>
			</span>
		);
	}

	return <span className='text-muted-foreground'>{String(data)}</span>;
}

export default function JsonViewer({
	data,
	basePath = '',
	defaultExpandDepth = 2,
	maxDepth = 6,
	highlightedPaths,
	expandedPaths,
	onPathClick
}: JsonViewerProps) {
	const safeHighlightedPaths = highlightedPaths ?? EMPTY_SET;
	const safeExpandedPaths = expandedPaths ?? EMPTY_SET;

	return (
		<JsonNode
			data={data}
			path={basePath}
			highlightedPaths={safeHighlightedPaths}
			expandedPaths={safeExpandedPaths}
			onPathClick={onPathClick}
			level={0}
			defaultExpandDepth={defaultExpandDepth}
			maxDepth={maxDepth}
		/>
	);
}
